import { faUser } from '@fortawesome/pro-light-svg-icons/faUser'
import { faUser as faUserSolid } from '@fortawesome/pro-solid-svg-icons/faUser'
import Button from 'antd/es/button'
import React, { useContext, useEffect, useState } from 'react'
import { GlobalNavContext } from '../../../GlobalNav'
import { Organization } from '../../../models'
import GlobalNavPopupContext, { PopupType } from '../../../PopupContext'
import { filterAccounts, getOrgIdFromLocalStorage, sortedAccountsByDate } from '../../../utils/helpers'
import { Dropdown } from '../../Dropdown'
import SearchBox from '../../SearchBox'
import AccountEntry from './AccountEntry'
import OrganizationEntry from './OrganizationEntry'
import OrgListSwitcher from './OrgListSwitcher'
import styles from './UserDropdown.scss'
import UserEntry from './UserEntry'

const perLineCharLength = 28 // characters that makes a whole line
const perLineHeight = 18 // heigh in px of a line (no margins)
const maxCharAccounts = 368 // each account has 18px per line, plus 20px of both margins

export interface DropDownContentProps {
  showOrgList: boolean
  showAccountList: boolean
  orgIdSelected: Organization
  onLogout: (event: React.MouseEvent) => void
}

const DropDownContent = ({ showAccountList, showOrgList, orgIdSelected, onLogout }: DropDownContentProps) => {
  const navContext = useContext(GlobalNavContext)
  const { account, accounts, organizations } = navContext
  const popupContext = useContext(GlobalNavPopupContext)

  const [searchText, setSearchText] = useState('')
  const [filteredAccounts, setFilteredAccounts] = useState([])

  useEffect(() => {
    setFilteredAccounts(filterAccounts(sortedAccountsByDate(accounts), searchText))
  },        [accounts, searchText])

  const organizationElements = organizations.map((org, idx) => (
    showAccountList
    ? (
      <OrganizationEntry
        organization={org}
        key={`${org.id}-${idx}`}
        isSearching={Boolean(searchText)}
        qty={organizations.length}
        idx={idx}
      >
        {
          filteredAccounts
            .filter((acc) => org.accounts.indexOf(acc.pCode) !== -1)
            .sort((acc) => account && account.id === acc.id ? -1 : 1)
            .map((acc) => (
              <AccountEntry
                data-cy={acc.id}
                isCurrent={account && account.id === acc.id}
                account={acc}
                onSelect={navContext.onAccountChanged}
                key={acc.id}
              />
            ))
        }
      </OrganizationEntry>
    ) : (
      <OrgListSwitcher
        key={`${org.id}-${org.name}`}
        orgSelected={orgIdSelected}
        organization={org}
      />
    )
  ))

  return (
    <div data-cy="userDrop" className={styles.drop_content}>
      {showAccountList &&
        <>
          <div style={{ paddingLeft: '23px', paddingRight: '25px' }}>
            <SearchBox
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder={'Find Accounts'}
            />
          </div>
          <div className={styles.organizations_container}>
            <div
              data-cy="ud-accounts"
              className={`${styles.list_items} ${(scrollMeasure(filteredAccounts)) ? 'showScrollIndicator' : ''}`}
            >
              {filteredAccounts.length > 0 ? (
                <div data-cy="listAccounts">{organizationElements}</div>
              ) : (
                  <div
                    data-cy="accountNotFound"
                    className={styles.drop_content__not_found}
                  >
                    <p>No results found</p>
                  </div>
                )

              }
            </div>
            <div data-cy="showAllAccounts">
              {accounts && (
                <Button
                  data-cy="showAllAccountsLink"
                  type="link"
                  onClick={() => popupContext.setOpenPopup(PopupType.ACCOUNTS_MODAL)}
                >
                  View as list
                </Button>
              )}
            </div>
          </div>
        </>

      }
      {
        showOrgList &&
        <>
          <div className={styles.organizations_element_container}>
            {organizationElements}
          </div>
          <div className={styles.divisor} />
        </>
      }
      <UserEntry logoutCb={onLogout} />
    </div>
  )
}

const scrollMeasure = (accounts): boolean => {
  const charCounter = accounts.reduce((accumulator, account) => {
    const perLine = Math.ceil((account && account.name.length) / perLineCharLength)
    let calcHeight

    switch (perLine) {
      case 1:
        calcHeight = ((perLineHeight * 1) + 20)
        break
      case 2:
        calcHeight = ((perLineHeight * 2) + 20)
        break
      default:
        calcHeight = ((perLineHeight * 3) + 20)
    }

    return accumulator = accumulator + calcHeight
    // tslint:disable-next-line: align
  }, 0)

  return charCounter > maxCharAccounts
}

export interface UserDropdownProps {
  showAccountList: boolean
  showUserInfo: boolean
  showOrgList: boolean
  onLogout: (event: React.MouseEvent) => void,
  className?: string
}

const UserDropdown = ({ className = '', showAccountList, showOrgList, showUserInfo, onLogout }: UserDropdownProps) => {
  const navContext = useContext(GlobalNavContext)
  const orgIdSelected = getOrgIdFromLocalStorage()

  const orgSelected = navContext.organizations.find(org => org.id === Number(orgIdSelected))

  const content = (
    <DropDownContent
      orgIdSelected={orgSelected}
      showAccountList={showAccountList}
      showOrgList={showOrgList}
      onLogout={onLogout}
    />
  )

  return (
    <Dropdown
      width={270}
      offset={20}
      icon={faUser}
      iconHover={faUserSolid}
      modalClassName={`${styles.dropdown} ${className}`}
      data-cy="userDropdown"
      type={PopupType.USER}
      actionIconChildren={(
        <>
          {!showOrgList && showUserInfo && (
            <div className={styles.user_info}>
              <h4>{navContext.user.name}</h4>
              <span>{navContext.user.email}</span>
            </div>
          )}
          {showOrgList && showUserInfo && (
            <div className={styles.user_info}>
              <h4>{(orgSelected || {}).name}</h4>
              <span>{navContext.user.name}</span>
            </div>
          )}
          {showAccountList && (
            <span className={styles.account_name}>{navContext.account && navContext.account.name}</span>
          )}
        </>
      )}
    >
      {content}
    </Dropdown>
  )
}

export default UserDropdown
